// digest-queries.ts — STEP-OV-DTC-DIGEST-MODULE
// Shared query module for home-digest data slices.
//
// PRIN-SIGNAL-INTEGRITY: Home tiles, Slack digest, tab drill-downs share the
// same query functions — never diverge. All slices flow through a single API
// call to /api/pwa/home-digest.
//
// In-memory cache: caches the full HomeDigestResponse for 5 seconds so multiple
// calls in the same render cycle don't trigger multiple network fetches.

import { api } from "../../api/client.js";
import type { HomeDigestResponse } from "../../api/types.js";

// ---------------------------------------------------------------------------
// In-memory response cache (5 second TTL)
// ---------------------------------------------------------------------------

interface CacheEntry {
  data: HomeDigestResponse;
  fetchedAt: number;
}

let _cache: CacheEntry | null = null;
const CACHE_TTL_MS = 5_000;

export async function getDigest(): Promise<HomeDigestResponse> {
  const now = Date.now();
  if (_cache !== null && now - _cache.fetchedAt < CACHE_TTL_MS) {
    return _cache.data;
  }
  const data = await api.getHomeDigest();
  _cache = { data, fetchedAt: Date.now() };
  return data;
}

/** Exposed for testing — clears the in-memory cache. */
export function _clearDigestCache(): void {
  _cache = null;
}

// ---------------------------------------------------------------------------
// Slice functions
// ---------------------------------------------------------------------------

/**
 * Count of pending proposals + operator-actionable attention items.
 * Derived from operator_queue.waiting array length.
 */
export async function operatorQueueCount(): Promise<number> {
  const d = await getDigest();
  return d.operator_queue.waiting.length;
}

/**
 * Phase close distance: open phase steps count + exit gate items count.
 */
export async function phaseCloseDistance(): Promise<{ open: number; exitGates: number }> {
  const d = await getDigest();
  return {
    open: d.roadmap.open_steps,
    exitGates: d.roadmap.exit_gate_items,
  };
}

/**
 * Current wave state, running track count, and merged PRs in last 24h.
 */
export async function waveStatus(): Promise<{
  activeWave: string | null;
  runningTracks: number;
  mergedPRs24h: number;
}> {
  const d = await getDigest();
  return {
    activeWave: d.wave.waveState !== "unknown" ? d.wave.waveState : null,
    runningTracks: d.wave.runningTracks,
    mergedPRs24h: d.wave.mergedPRs24h,
  };
}

/**
 * System health status.
 * isHealthy = ALL fail-closed signals green (ciState green + criticalVlrs === 0 + services ok).
 *
 * TODO: criticalVlrs is not in HomeDigestResponse — defaulting to 0.
 * When API exposes critical_vlrs field, replace the default.
 */
export async function healthStatus(): Promise<{
  deployedSha: string;
  mainSha: string;
  ciState: string;
  criticalVlrs: number;
  isHealthy: boolean;
}> {
  const d = await getDigest();
  const ciOk = d.wave.ciState === "green" || d.wave.ciState === "success";
  const servicesOk = d.health.services_health === "ok";
  const autoCosOk = d.health.auto_cos_gate?.overall === "GREEN";
  // TODO: criticalVlrs — not in HomeDigestResponse — defaulting to 0
  const criticalVlrs = 0;
  const isHealthy = ciOk && servicesOk && autoCosOk && criticalVlrs === 0;
  return {
    deployedSha: d.wave.deploySHA,
    mainSha: d.wave.mainSHA,
    ciState: d.wave.ciState,
    criticalVlrs,
    isHealthy,
  };
}

/**
 * Milestone certificate closure status.
 * Returns milestone certificates issued, in review, and next candidate.
 *
 * TODO: "issued" and "inReview" certificate counts are not directly in
 * HomeDigestResponse.closure. Deriving from milestone_trajectories:
 * - issued = milestones at 100% completion
 * - inReview = milestones with po_review_state !== "none"
 * - nextCandidate = first milestone not yet complete (by title)
 */
export async function closureStatus(): Promise<{
  issued: number;
  inReview: number;
  nextCandidate: string | null;
}> {
  const d = await getDigest();
  const trajectories = d.closure.milestone_trajectories;
  const issued = trajectories.filter((m) => m.completion_pct >= 100).length;
  const inReview = trajectories.filter(
    (m) => m.po_review_state !== "none" && m.completion_pct < 100,
  ).length;
  const nextCandidate =
    trajectories.find((m) => m.completion_pct < 100)?.title ?? null;
  return { issued, inReview, nextCandidate };
}

/**
 * Cost and dispatch metrics — real spend data per DEC-244.
 */
export async function costStatus(): Promise<{
  todayUsage: number;
  allocation: number | null;
  capEvents24h: number;
}> {
  const d = await getDigest();
  return {
    todayUsage: d.cost.todayUsage,
    allocation: d.cost.monthlyAllocation,
    capEvents24h: d.cost.capEvents24h,
  };
}
